import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiFeather } from "react-icons/fi";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import { PasswordField } from "../../../ui/PasswordField";
import * as S from "../Login.styled";
import { useAuth } from "../../../context/AuthContext";
import { getHomeRouteForRole } from "../../../Utils/homeRoute";

import SocialAuthButtons, { SocialAuthNotice } from "../SocialAuth";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginPage = () => {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "success" });
  const [approvalPending, setApprovalPending] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();

  const validate = (values) => {
    const next = {};
    if (!values.email.trim()) {
      next.email = t("login.field_required", { defaultValue: "This field is required" });
    } else if (!EMAIL_RE.test(values.email.trim())) {
      next.email = t("login.email_invalid", { defaultValue: "Enter a valid email address" });
    }
    if (!values.password) {
      next.password = t("login.field_required", { defaultValue: "This field is required" });
    }
    return next;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const next = { ...credentials, [name]: value };
    setCredentials(next);
    if (touched[name]) setErrors(validate(next));
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(credentials));
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    window.setTimeout(() => {
      setNotification({ message: "", type: "success" });
    }, 3000);
  };

  const getErrorMessage = (error) => {
    const data = error?.response?.data;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (typeof data === "string" && data.trim()) return data;
    return t("login.error", { defaultValue: "Login failed. Please check your credentials." });
  };

  const requiresEmailConfirmation = (error, message) => {
    const code = error?.response?.data?.code;
    if (code === "EMAIL_CONFIRMATION_REQUIRED") return true;
    return /confirm your email/i.test(message || "");
  };

  /**
   * A farmer whose account exists but has not been approved yet. The server's
   * own message is raw English ("Villager account pending approval.") which is
   * wrong for an Albanian-default UI, so this is translated locally.
   */
  const awaitingApproval = (error, message) => {
    const code = error?.response?.data?.code;
    if (code === "VILLAGER_APPROVAL_REQUIRED") return true;
    return /pending approval/i.test(message || "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validate(credentials);
    setErrors(validation);
    setTouched({ email: true, password: true });
    if (Object.keys(validation).length > 0) {
      const first = ["email", "password"].find((key) => validation[key]);
      if (first) document.getElementById(first)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const { role } = await login(credentials);
      showNotification(t("login.success", { defaultValue: "Login successful!" }), "success");
      window.setTimeout(() => navigate(getHomeRouteForRole(role)), 1000);
    } catch (error) {
      setSubmitting(false);
      const message = getErrorMessage(error);
      if (requiresEmailConfirmation(error, message)) {
        showNotification(
          t("login.confirm_required_redirect", {
            defaultValue: "Please confirm your email first. Redirecting...",
          }),
          "error"
        );
        const email = encodeURIComponent((credentials.email || "").trim());
        window.setTimeout(() => {
          navigate(`/confirm-email${email ? `?email=${email}` : ""}`);
        }, 800);
        return;
      }
      if (awaitingApproval(error, message)) {
        // Deliberately NOT the 3s toast: there is nowhere to redirect to and
        // nothing the farmer can do right now, so the explanation has to persist.
        setApprovalPending(true);
        return;
      }
      showNotification(message, "error");
    }
  };

  return (
    <S.Page>
      <S.FormWrapper>
        <S.FormHeader>
          <S.HeaderIcon aria-hidden="true">
            <FiFeather />
          </S.HeaderIcon>
          <h1>{t("login.welcome_back", { defaultValue: "Welcome back" })}</h1>
          <S.Subtitle>
            {t("login.subtitle_login", {
              defaultValue: "Sign in to keep shopping fresh from local farms.",
            })}
          </S.Subtitle>
        </S.FormHeader>
        <SocialAuthNotice />
        <SocialAuthButtons />
        <S.Form onSubmit={handleSubmit} noValidate>
          <Input
            id="email"
            name="email"
            label={t("login.email", { defaultValue: "Email" })}
            placeholder={t("login.email_placeholder", { defaultValue: "example@example.com" })}
            type="email"
            inputMode="email"
            autoComplete="email"
            value={credentials.email}
            onChange={handleChange}
            onBlur={handleBlur}
            errorText={touched.email ? errors.email : undefined}
            required
          />
          <PasswordField
            id="password"
            name="password"
            label={t("login.password", { defaultValue: "Password" })}
            placeholder={t("login.password_placeholder", { defaultValue: "Password" })}
            autoComplete="current-password"
            value={credentials.password}
            onChange={handleChange}
            onBlur={handleBlur}
            errorText={touched.password ? errors.password : undefined}
            required
          />
          <S.ToggleText>
            <S.ToggleAction type="button" onClick={() => navigate("/forgot-password")}>
              {t("login.forgot_password", { defaultValue: "Forgot password?" })}
            </S.ToggleAction>
          </S.ToggleText>
          {approvalPending ? (
            <S.Notification $type="error" role="status" data-testid="approval-pending">
              {t("villager.pending.login_blocked", {
                defaultValue:
                  "Your farm is still being reviewed. We'll email you as soon as it's approved, and then you can sign in.",
              })}
            </S.Notification>
          ) : null}
          {notification.message ? (
            <S.Notification $type={notification.type} role="status">
              {notification.message}
            </S.Notification>
          ) : null}
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            {t("login.submit", { defaultValue: "Login" })}
          </Button>
        </S.Form>
        <S.ToggleText>
          {t("login.toggle_new", { defaultValue: "Don't have an account?" })}{" "}
          <S.ToggleAction type="button" onClick={() => navigate("/signup")}>
            {t("login.toggle_signup", { defaultValue: "Sign Up" })}
          </S.ToggleAction>
        </S.ToggleText>
      </S.FormWrapper>
    </S.Page>
  );
};

export default LoginPage;
